/**
 * Componente de gráfico de línea para comparativas
 * Line chart component for comparisons
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from 'recharts';
import {
  FilesComparisonDataPoint,
  WebhooksComparisonDataPoint,
  ApiComparisonDataPoint,
} from '../../models/TrackingApiResponse';

type ComparisonDataPoint =
  | FilesComparisonDataPoint
  | WebhooksComparisonDataPoint
  | ApiComparisonDataPoint;

interface ComparisonChartProps {
  currentData: ComparisonDataPoint[];
  previousData: ComparisonDataPoint[];
  type: 'files' | 'webhooks' | 'api';
  isLoading?: boolean;
}

const COLORS = {
  current: '#296dff', // Azul para período actual
  previous: '#94a3b8', // Gris para período anterior
};

// Helper function para obtener el valor según el tipo
const getValue = (point: ComparisonDataPoint, type: string): number => {
  switch (type) {
    case 'files':
      return (point as FilesComparisonDataPoint).files_received || 0;
    case 'webhooks':
      return (point as WebhooksComparisonDataPoint).webhooks_received || 0;
    case 'api':
      return (point as ApiComparisonDataPoint).api_calls || 0;
    default:
      return 0;
  }
};

export const ComparisonChart = ({
  currentData,
  previousData,
  type,
  isLoading = false,
}: ComparisonChartProps) => {
  const { t } = useTranslation('trackingModule');

  // Preparar datos combinados para el gráfico
  const chartData = useMemo(() => {
    if (!currentData || !previousData) return [];

    // Crear un mapa de horas para combinar datos
    const hourMap = new Map<string, any>();

    // Agregar datos del período actual
    currentData.forEach((point) => {
      hourMap.set(point.hour, {
        hour: point.hour,
        current: getValue(point, type),
        previous: 0,
      });
    });

    // Agregar datos del período anterior
    previousData.forEach((point) => {
      const existing = hourMap.get(point.hour);
      if (existing) {
        existing.previous = getValue(point, type);
      } else {
        hourMap.set(point.hour, {
          hour: point.hour,
          current: 0,
          previous: getValue(point, type),
        });
      }
    });

    return Array.from(hourMap.values()).sort((a, b) =>
      a.hour.localeCompare(b.hour)
    );
  }, [currentData, previousData, type]);

  const getCurrentLabel = (): string => {
    switch (type) {
      case 'files':
        return t('dashboard.files.files_received');
      case 'webhooks':
        return t('dashboard.webhooks.webhooks_received');
      case 'api':
        return t('dashboard.api.api_calls');
      default:
        return '';
    }
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card-bg border border-card-border rounded-md shadow-lg p-3 z-50">
          <p className="text-sm text-subtext mb-2">
            {payload[0]?.payload?.hour || ''}
          </p>
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-sm"
                style={{ backgroundColor: entry.color }}
              />
              <div>
                <p className="font-medium text-text text-sm">
                  {entry.name}: {entry.value}
                </p>
              </div>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  if (isLoading || chartData.length === 0) {
    return (
      <div className="w-full h-[256px] flex items-center justify-center">
        <p className="text-subtext">{t('loading.message')}</p>
      </div>
    );
  }

  return (
    <div className="w-full" style={{ height: '256px' }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData}
          margin={{ top: 10, right: 10, left: -10, bottom: 5 }}
        >
          <defs>
            <linearGradient id="gradient-current" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={COLORS.current} stopOpacity={0.1} />
              <stop offset="100%" stopColor={COLORS.current} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gradient-previous" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={COLORS.previous} stopOpacity={0.1} />
              <stop offset="100%" stopColor={COLORS.previous} stopOpacity={0} />
            </linearGradient>
          </defs>

          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="currentColor"
            strokeOpacity={0.08}
          />

          <XAxis
            dataKey="hour"
            axisLine={false}
            tickLine={false}
            tick={{ fill: 'currentColor', fontSize: 11, opacity: 0.5 }}
            dy={10}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fill: 'currentColor', fontSize: 11, opacity: 0.5 }}
            width={35}
            tickFormatter={(value) => value.toLocaleString()}
          />

          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ paddingTop: '20px' }}
            iconType="line"
            formatter={(value) => {
              if (value === 'current') return t('dashboard.period.current');
              if (value === 'previous') return t('dashboard.period.previous');
              return value;
            }}
          />

          <Line
            type="monotone"
            dataKey="current"
            name="current"
            stroke={COLORS.current}
            strokeWidth={2.5}
            dot={false}
            activeDot={{ r: 4, fill: COLORS.current, strokeWidth: 0 }}
          />
          <Line
            type="monotone"
            dataKey="previous"
            name="previous"
            stroke={COLORS.previous}
            strokeWidth={2.5}
            strokeDasharray="5 5"
            dot={false}
            activeDot={{ r: 4, fill: COLORS.previous, strokeWidth: 0 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

